import { Option, Select } from '@repo/ui';
import React from 'react';
import { Col, Label } from 'reactstrap';

/**
 * Componente visual de filtros por Organização e Curso baseado nos perfis da pessoa.
 *
 * Deve ser usado junto com o hook `useEntityAndCourseFiltersByProfiles`, que fornece os dados e os handlers.
 *
 * Exibe dois selects dinâmicos que respeitam as seguintes regras:
 *
 * (Apenas perfis válidos são considerados para exibição dos filtros)
 *
 * * 1. **Vários perfis válidos e várias Organizações/Cursos**:
 *    - Exibe os dois selects com placeholders: "Todas as Organizações" e "Todos os Cursos".
 *
 * 2. **Apenas um perfil válido com uma Organização/Curso**:
 *    - Os campos são preenchidos automaticamente com a única Organização e Curso disponíveis.
 *
 * 3. **Vários perfis e uma Organização, mas vários Cursos**:
 *    - Autopreenche a Organização e exibe o campo de Curso com placeholder "Todos os Cursos".
 *
 * 4. **Vários perfis e uma Organização/Curso apenas**:
 *    - Ambos os campos são preenchidos automaticamente.
 *
 * - Os selects são atualizados conforme o estado retornado pelo hook.
 *
 * Idealizado para ser utilizado em detalhes de uma Pessoa com múltiplos vínculos, de acordo com o perfil.
 *
 * @component
 *
 * @param {EntityAndCourseFiltersByProfilesProps} props
 * @prop {boolean} hasValidProfile - Indica se existem perfis válidos para exibição dos filtros.
 * @prop {Option[]} courses - Lista de cursos no formato { label, value }.
 * @prop {Option[]} organizations - Lista de organizações no formato { label, value }.
 * @prop {Option | null} selectedCourse - Curso atualmente selecionado.
 * @prop {Option | null} selectedOrganization - Organização atualmente selecionada.
 * @prop {number} [orgColSize=4] - Tamanho da coluna Bootstrap para o select de Organização.
 * @prop {number} [courseColSize=4] - Tamanho da coluna Bootstrap para o select de Curso.
 * @prop {(course: Option | null) => void} onCourseChange - Função chamada ao alterar o curso.
 * @prop {(organization: Option | null) => void} onOrganizationChange - Função chamada ao alterar a organização.
 * @prop {boolean} [touched=false] - Indica se os campos foram tocados (obrigatório - deve ser passado pelo hook `useEntityAndCourseFiltersTouched`).
 *
 * @returns {JSX.Element | null} JSX com os selects ou `null` caso não haja perfis válidos.
 *
 * @example
 * ```tsx
 * <EntityAndCourseFiltersByProfiles
 *   hasValidProfile={hasValidProfile}
 *   organizations={orgOptions}
 *   courses={courseOptions}
 *   selectedOrganization={selectedOrganization}
 *   selectedCourse={selectedCourse}
 *   onCourseChange={onCourseChange}
 *   onOrganizationChange={onOrganizationChange}
 *   touched={touched}
 * />
 * ```
 */

interface EntityAndCourseFiltersByProfilesProps {
  hasValidProfile: boolean;
  courses: Option[];
  organizations: Option[];
  selectedCourse: Option | null;
  selectedOrganization: Option | null;
  orgColSize?: number;
  courseColSize?: number;
  onCourseChange: (course: Option | null) => void;
  onOrganizationChange: (organization: Option | null) => void;
  touched: { org: boolean; course: boolean };
}

export const EntityAndCourseFiltersByProfiles = ({
  courses,
  organizations,
  onCourseChange,
  selectedCourse,
  hasValidProfile,
  onOrganizationChange,
  selectedOrganization,
  orgColSize = 4,
  courseColSize = 4,
  touched,
}: EntityAndCourseFiltersByProfilesProps) => {
  const orgLength = organizations.length;
  const courseLength = courses.length;

  if (!hasValidProfile) return null;

  return (
    <React.Fragment>
      <Col md={orgColSize}>
        <Label className="form-label">Organização</Label>
        <Select
          options={organizations}
          value={selectedOrganization}
          placeholder={orgLength > 1 ? 'Todas as Organizações' : ''}
          onChange={(organization) => {
            onOrganizationChange(organization);
          }}
          invalid={touched.org && !selectedOrganization}
        />
      </Col>

      <Col md={courseColSize}>
        <Label className="form-label">Curso</Label>
        <Select
          options={courses}
          value={selectedCourse}
          placeholder={courseLength > 1 ? 'Todos os Cursos' : ''}
          onChange={(course) => {
            onCourseChange(course);
          }}
          invalid={touched.course && !selectedCourse}
        />
      </Col>
    </React.Fragment>
  );
};
